import { Icon } from "@captions-web/icons";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { t } from "i18next";
import { Fragment, useMemo } from "react";
import { toast } from "sonner";

import { MirageLogoSmall } from "~/components/mirage-logo";
import { NoiseGrid } from "~/components/noise-grid";
import { PriceBox } from "~/modules/checkout/components/price-box";
import { SubscriptionCheckoutForm } from "~/modules/checkout/components/subscription-checkout-form";
import { getCheckoutInfoQuery } from "~/modules/checkout/network/queries";
import {
  getBusinessPlanPriceInfo,
  getProductAndPriceInfo,
  getStripeKeyMode,
} from "~/modules/checkout/utils";
import { css } from "~/styled-system/css";

export const Route = createFileRoute("/pay/checkout")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();

  const keyMode = getStripeKeyMode();
  const { data } = getCheckoutInfoQuery.useSuspenseQuery({
    params: { stripeKeyMode: keyMode },
  });

  const [{ planInfo, priceInfo }, priceDisplayInfo] = useMemo(
    () => [getProductAndPriceInfo(data, "business", "monthly"), getBusinessPlanPriceInfo(data)],
    [data]
  );

  const onSubscriptionActivated = () => {
    toast.success("Payment successful, you now have access to Mirage Studio");
    return navigate({ to: "/home" });
  };

  if (!planInfo || !priceInfo || !priceDisplayInfo) {
    throw new Error("No plan info or price info");
  }

  const content = (
    <div className={boxStyles}>
      <aside className={css({ display: "flex", flexDirection: "column", gap: 24, padding: 24 })}>
        <MirageLogoSmall />
        <h1 className={css({ textStyle: "heading-4", color: "$gray.200" })}>Mirage Studio</h1>
        <div className={css({ display: "flex", gap: 8 })}>
          <Icon name="check" className={css({ flexShrink: 0 })} />
          <div className={css({ display: "flex", flexDirection: "column", gap: 8 })}>
            <span className={css({ textStyle: "body-2-intense", color: "$gray.100" })}>
              {priceDisplayInfo?.includedCredits.toLocaleString()}{" "}
              {t("mirage-studio:checkout.monthly-credits")}
            </span>
            <span className={css({ textStyle: "body-3-subtle", color: "$gray.400" })}>
              {t("mirage-studio:checkout.value-1-credits.description")}
            </span>
          </div>
        </div>
        <div className={css({ display: "flex", gap: 8 })}>
          <Icon name="check" className={css({ flexShrink: 0 })} />
          <div className={css({ display: "flex", flexDirection: "column", gap: 8 })}>
            <span className={css({ textStyle: "body-2-intense", color: "$gray.100" })}>
              {t("mirage-studio:checkout.team-access.title")}
            </span>
            <span className={css({ textStyle: "body-3-subtle", color: "$gray.400" })}>
              {t("mirage-studio:checkout.team-access.description")}
            </span>
          </div>
        </div>
        <PriceBox priceDisplayInfo={priceDisplayInfo}>
          <span
            className={css({
              textStyle: "body-3-subtle",
              color: "$gray.100",
              textTransform: "capitalize",
            })}
          >
            {planInfo.name} {t("common:subscription")}
          </span>
        </PriceBox>
      </aside>
      <div
        className={css({
          flex: 1,
          display: "flex",
          flexDirection: "column",
          padding: 24,
          pointerEvents: "auto",
        })}
      >
        <SubscriptionCheckoutForm
          planInfo={planInfo}
          priceInfo={priceInfo}
          overridePriceCents={priceDisplayInfo.priceCents}
          onSubscriptionActivated={onSubscriptionActivated}
        />
      </div>
    </div>
  );

  return (
    <Fragment>
      <NoiseGrid />
      <div className={boxContainerStyles}>
        {content}
        {keyMode === "test" && <div className={testmodeBadgeStyles}>Test mode</div>}
      </div>
    </Fragment>
  );
}

const boxContainerStyles = css({
  position: "relative",
  display: "flex",
  width: "100%",
  maxWidth: "950px",
});

const boxStyles = css({
  backgroundColor: "$surface.translucent",
  backdropFilter: "blur(2px)",
  boxShadow: "$highlight",

  position: "relative",
  display: "grid",
  gridTemplateColumns: "360px 1fr",
  alignItems: "stretch",
  width: "100%",

  minHeight: "810px",
  overflow: "auto",

  borderRadius: "32px",
  pointerEvents: "none",

  "@media (max-height: 810px)": {
    maxHeight: "95dvh",
    minHeight: "auto",
  },
});

const testmodeBadgeStyles = css({
  position: "absolute",
  top: "-30px",
  right: "20px",
  padding: "2px 6px",

  textStyle: "body-3-subtle",
  backgroundColor: "#ff8f0e",
  borderRadius: "8px",
  color: "white",
});
